import React, { useEffect } from "react";
import DashboardWrapper from "../../layout/DashboardWrapper/DashboardWrapper";
import ProfileForm from "../../components/ProfileForm/ProfileForm";
import Grid from "@mui/material/Grid";
import styles from "../../styles/components/profileform.module.scss";
import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";

import CustomButton from "../../ui/CustomButton/CustomButton";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomInput from "../../components/CustomInput/CustomInput";
import useNotiStack from "../../hooks/useNotistack";
import * as yup from "yup";
import validationText from "../../json/messages/validationText";
import BackdropLoader from "../../ui/BackdropLoader/BackdropLoader";
import {
  GetProfileDetails,
  UpdateMCAdminUser,
} from "../../api/functions/user.api";
import { administratorList } from "../../json/commonData/jsonListData";
import { adminMemberData, isPasswordMismatch } from "../../services/utils";

const schema = yup.object().shape(
  {
    firstName: yup
      .string()
      .trim()
      .required(
        <p style={{ fontSize: "12px" }}>{validationText.error.first_name}</p>
      ),
    lastName: yup.string().trim().required().label("Last name"),
    email: yup
      .string()
      .trim()
      .required(
        <p style={{ fontSize: "12px" }}>{validationText.error.enter_email}</p>
      )
      .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "Email address is not valid")
      .email(validationText.error.email_format),
    adminLevel: yup.string().required(),
    password: yup
      .string()
      //  .trim()
      .when("password", {
        is: (value) => value,
        then: (rule) => rule.min(8).max(255).label("password"),
      })
      .when("confirmPassword", {
        is: (value) => value,
        then: (rule) => rule.min(8).max(255).label("password"),
      }),
    confirmPassword: yup.string().when("password", {
      is: (value) => value,
      then: (rule) =>
        rule
          .required()
          // .oneOf([yup.ref("password"), null])
          .label("Confirm password"),
    }),
  },
  [
    ["password", "password"],
    ["confirmPassword", "password"],
  ]
);

export default function UserProfileEditUser() {
  const params = useParams();
  const { toastSuccess } = useNotiStack();
  const navigate = useNavigate();
  const user = adminMemberData();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    getValues,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const isNotMatchPassword = isPasswordMismatch(
    watch("password"),
    watch("confirmPassword")
  );
  const adminUsersDetails = useQuery(["adminUsersAccDetails", params?.id], () =>
    GetProfileDetails({ id_adminuser: Number(params?.id) }, user)
  );

  const {
    mutateAsync: mutateAsyncUpdateMCAdminUserDetails,
    isLoading: updatingProfileStatus,
  } = useMutation({
    mutationFn: (payloadData) => UpdateMCAdminUser(payloadData, user),
    onSuccess: (response) => {
      if (response.Status === 1) {
        toastSuccess("Account details updated successfully.");
        navigate("/dashboard/user-profile");
        queryClient.invalidateQueries(["adminAccountList"]);
      }
    },
  });

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append("firstName", data?.firstName?.trim());
    formData.append("lastName", data?.lastName?.trim());
    formData.append("email", data?.email?.trim());
    formData.append("administrator", Number(data?.adminLevel));
    formData.append("id_adminuser", params?.id);

    if (data?.password?.length > 0) {
      formData.append("password", data?.password?.trim());
    }

    mutateAsyncUpdateMCAdminUserDetails(formData);
  };

  const closeTopage = () => {
    navigate("/dashboard/user-profile");
  };

  useEffect(() => {
    reset({
      firstName: adminUsersDetails?.data?.firstName,
      lastName: adminUsersDetails?.data?.lastName,
      email: adminUsersDetails?.data?.email,
      adminLevel: adminUsersDetails?.data?.administrator.toString(),
      password: "",
      confirmPassword: "",
    });
  }, [adminUsersDetails?.data]);

  return (
    <DashboardWrapper showBtmHeader={false} isList={false}>
      <ProfileForm formHeader="Edit User">
        <BackdropLoader open={adminUsersDetails?.isLoading} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container rowSpacing={{ md: 4, xs: 2.5 }} columnSpacing={2}>
            <Grid item xs={12} lg={5} md={6} sm={6}>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>First Name</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="text"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.firstName)}
                          helperText={errors?.firstName?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Last Name</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="text"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.lastName)}
                          helperText={errors?.lastName?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={`${styles.profile_grid} ${styles.mb_0}`}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Email</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="email"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          // type="email"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.email)}
                          helperText={errors?.email?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={5} md={6} sm={6}>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Admin User Level</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="adminLevel"
                      render={({ field: { onChange, value } }) => (
                        <Box className={styles.selectWrap}>
                          <Select
                            className={styles.cmn_slct}
                            defaultValue=""
                            id="demo-simple-select"
                            onChange={onChange}
                            // value={value}
                            value={value ? value : ""}
                          >
                            {administratorList?.map((e, index) => (
                              <MenuItem key={e?._id} value={e?._id}>
                                <Typography
                                  variant="body1"
                                  className="menu_para"
                                >
                                  {e?.name}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      )}
                    />
                    {errors.adminLevel && (
                      <p className={`${styles.error_text} ${styles.err_txt2}`}>
                        {validationText.error.adminLevel}
                      </p>
                    )}
                  </Box>
                </Stack>
              </Box>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Password</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="password"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="password"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.password)}
                          helperText={errors?.password?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={`${styles.profile_grid} ${styles.mb_0}`}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Confirm Password</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="confirmPassword"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="password"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.confirmPassword)}
                          helperText={errors?.confirmPassword?.message}
                        />
                      )}
                    />
                    {getValues("confirmPassword") && isNotMatchPassword && (
                      <p className={styles.error_text}>
                        {validationText.error.password_mismatch}
                      </p>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={2} md={12} sm={12}>
              <Box className={styles.profile_btn}>
                <Box className={styles.black_btn}>
                  <CustomButton onClick={closeTopage}>
                    <Typography variant="caption">Close</Typography>
                  </CustomButton>
                </Box>

                <CustomButton
                  type="submit"
                  disabled={isDirty === false || isNotMatchPassword}
                >
                  {" "}
                  <Typography variant="caption">
                    {" "}
                    {updatingProfileStatus ? "Saving.." : "Save Profile"}
                  </Typography>
                </CustomButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </ProfileForm>
    </DashboardWrapper>
  );
}
